/**
 * Test Script for Email Classification Workflow
 *
 * Simulates receiving emails and shows how the workflow classifies them.
 *
 * Usage:
 *   import { testEmailClassificationWorkflow } from '@business-assistant/shared';
 *   testEmailClassificationWorkflow();
 */

import { eventBus } from '../eventBus';
import { triggerMatcher } from '../triggerMatcher';
import { executeWorkflow } from '../workflowEngine';
import { registerAllStepExecutors } from '../steps';
import { emailClassificationWorkflow } from './emailClassificationWorkflow';
import type { EngineEvent, InAppNotification } from '../types';

const log = (msg: string) => console.log(`[EmailWorkflowTest] ${msg}`);

/**
 * Sample emails for testing
 */
const TEST_EMAILS = {
  // Work email from potential client
  workEmail: {
    from: 'marco.rossi@acmecorp.it',
    fromName: 'Marco Rossi',
    subject: 'Richiesta preventivo sito web',
    body: `Buongiorno,

siamo Acme Corp e vorremmo richiedere un preventivo per la realizzazione del nostro nuovo sito web aziendale.

Avremmo bisogno di:
- Homepage con presentazione aziendale
- Pagina servizi
- Form contatti
- Area riservata clienti

Potete inviarci una proposta?

Cordiali saluti,
Marco Rossi
Acme Corp S.r.l.`,
  },

  // Advertisement email
  advertisementEmail: {
    from: 'noreply@marketing-platform.com',
    fromName: 'Marketing Platform',
    subject: '🎉 OFFERTA SPECIALE: -50% su tutti i piani!',
    body: `Non perdere questa occasione unica!

Solo per oggi, sconto del 50% su tutti i nostri piani premium.

Clicca qui per approfittare dell'offerta >>>

Disiscrivi | Privacy Policy`,
  },

  // Spam email
  spamEmail: {
    from: 'prince.nigeria@freemail.xyz',
    fromName: 'Prince of Nigeria',
    subject: 'URGENT: $10,000,000 waiting for you!!!',
    body: `Dear Friend,

I am Prince Abubakar from Nigeria. I have $10,000,000 USD that I need to transfer to your account.

Please send me your bank details immediately.

God bless you,
Prince Abubakar`,
  },

  // Personal email
  personalEmail: {
    from: 'mamma@gmail.com',
    fromName: 'Mamma',
    subject: 'Ci vediamo domenica?',
    body: `Ciao tesoro,

volevo sapere se vieni a pranzo domenica.
Preparo le lasagne che ti piacciono tanto!

Un bacio,
Mamma`,
  },
};

/**
 * Run a single email through the workflow
 */
async function testSingleEmail(
  emailType: keyof typeof TEST_EMAILS,
  pushCallback: (notif: InAppNotification) => void
): Promise<void> {
  const email = TEST_EMAILS[emailType];
  log(`\n📧 Testing: ${emailType}`);
  log(`   From: ${email.fromName} <${email.from}>`);
  log(`   Subject: ${email.subject}`);

  // Create email received event
  const event: EngineEvent = {
    id: `test-email-${Date.now()}`,
    type: 'email.received',
    entityType: 'email',
    entityId: `eml-test-${Date.now()}`,
    data: {
      from: email.from,
      fromName: email.fromName,
      subject: email.subject,
      body: email.body,
    },
    timestamp: new Date().toISOString(),
    userId: 'system',
    source: 'polling',
  };

  // Execute workflow
  const context = await executeWorkflow(emailClassificationWorkflow, event);

  log(`   Status: ${context.status}`);

  // Check for notification in pipeline
  if (context.pipelineData.notification) {
    const notif = context.pipelineData.notification as InAppNotification;
    log(`   📬 Notification created:`);
    log(`      Badge: ${notif.badge}`);
    log(`      Title: ${notif.title}`);
    log(`      Priority: ${notif.priority}`);
    if (notif.actions) {
      log(`      Actions: ${notif.actions.map(a => a.label).join(', ')}`);
    }
    pushCallback(notif);
  }

  // Show classification result
  if (context.pipelineData.emailClassification) {
    const classification = context.pipelineData.emailClassification as any;
    log(`   🤖 AI Classification: ${classification.classification} (${Math.round(classification.confidence * 100)}%)`);
    log(`      Reason: ${classification.reason}`);
  }

  // Show client match result
  if (context.pipelineData.clientMatch) {
    const match = context.pipelineData.clientMatch as any;
    if (match.found) {
      log(`   👤 Client matched: ${match.contactName} (${match.company})`);
    } else {
      log(`   👤 Client not found — will ask to create`);
    }
  }
}

/**
 * Run the complete test suite
 */
export async function testEmailClassificationWorkflow(): Promise<{
  notifications: InAppNotification[];
  success: boolean;
}> {
  log('========================================');
  log('Email Classification Workflow Test');
  log('========================================');

  const notifications: InAppNotification[] = [];
  const pushCallback = (notif: InAppNotification) => notifications.push(notif);

  // Register step executors
  registerAllStepExecutors();

  // Register workflow in trigger matcher (for completeness, though we call directly)
  triggerMatcher.register(emailClassificationWorkflow);

  try {
    // Test each email type
    for (const emailType of Object.keys(TEST_EMAILS) as (keyof typeof TEST_EMAILS)[]) {
      await testSingleEmail(emailType, pushCallback);
    }

    log('\n========================================');
    log(`Test Complete: ${notifications.length} notifications created`);
    log('========================================');

    // Summary
    log('\n📋 Notification Summary:');
    for (const notif of notifications) {
      log(`   ${notif.badge} — ${notif.title}`);
    }

    return { notifications, success: true };
  } catch (err) {
    log(`\n❌ Test failed: ${err}`);
    return { notifications, success: false };
  } finally {
    // Cleanup
    triggerMatcher.unregister(emailClassificationWorkflow.id);
  }
}

/**
 * Quick test with a single work email
 */
export async function testWorkEmailOnly(): Promise<InAppNotification | null> {
  registerAllStepExecutors();

  const email = TEST_EMAILS.workEmail;
  const event: EngineEvent = {
    id: `test-email-${Date.now()}`,
    type: 'email.received',
    entityType: 'email',
    entityId: `eml-test-${Date.now()}`,
    data: {
      from: email.from,
      fromName: email.fromName,
      subject: email.subject,
      body: email.body,
    },
    timestamp: new Date().toISOString(),
    userId: 'system',
    source: 'polling',
  };

  const context = await executeWorkflow(emailClassificationWorkflow, event);
  console.log('[TestWorkEmail] Classification:', context.pipelineData.emailClassification);
  console.log('[TestWorkEmail] Client match:', context.pipelineData.clientMatch);

  return (context.pipelineData.notification as InAppNotification) || null;
}

/**
 * Test with a custom email - for manual testing
 */
export async function testCustomEmail(email: {
  from: string;
  fromName?: string;
  subject: string;
  body: string;
}): Promise<{
  classification: any;
  clientMatch: any;
  notification: InAppNotification | null;
  context: any;
}> {
  registerAllStepExecutors();

  console.log('[TestCustomEmail] Testing email from:', email.from);
  console.log('[TestCustomEmail] Subject:', email.subject);

  const event: EngineEvent = {
    id: `test-email-${Date.now()}`,
    type: 'email.received',
    entityType: 'email',
    entityId: `eml-test-${Date.now()}`,
    data: {
      from: email.from,
      fromName: email.fromName || email.from.split('@')[0],
      subject: email.subject,
      body: email.body,
    },
    timestamp: new Date().toISOString(),
    userId: 'system',
    source: 'manual-test',
  };

  const context = await executeWorkflow(emailClassificationWorkflow, event);

  const result = {
    classification: context.pipelineData.emailClassification,
    clientMatch: context.pipelineData.clientMatch,
    notification: (context.pipelineData.notification as InAppNotification) || null,
    context: {
      status: context.status,
      log: context.log,
      pipelineData: context.pipelineData,
    },
  };

  console.log('[TestCustomEmail] === RESULT ===');
  console.log('[TestCustomEmail] Classification:', JSON.stringify(result.classification, null, 2));
  console.log('[TestCustomEmail] Client Match:', JSON.stringify(result.clientMatch, null, 2));
  console.log('[TestCustomEmail] Notification:', result.notification ? JSON.stringify(result.notification, null, 2) : 'None');
  console.log('[TestCustomEmail] Workflow Status:', context.status);
  console.log('[TestCustomEmail] Steps executed:', context.log.map(l => `${l.stepName}: ${l.status}`).join(' → '));

  return result;
}
